import { afterNextRender, Component, forwardRef, HostBinding, input, linkedSignal, output, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractControl } from '../abstract-control/abstract-control';
import { CSSValuePipe } from '@pipes/css-value-pipe';

@Component({
  selector: 'app-input-text',
  standalone: true,
  imports: [ MatTooltipModule, CSSValuePipe ],
  templateUrl: './input-text.html',
  styleUrl: './../abstract-control/abstract-control.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputText),
      multi: true,
    },
  ],
})
export class InputText extends AbstractControl<string | undefined, string> {
  // Inputs
  override $value = input<string | undefined>(undefined, { alias: 'value' });
  $_value = linkedSignal(this.$value);
  $type = input<'text' | 'email' | 'password'>('text', { alias: 'type' });
  $inputWidth = input<string>('100%', { alias: 'inputWidth' });
  $hostWidth = input<string>('auto', { alias: 'hostWidth' });
  $placeholder = input<string>('', { alias: 'placeholder' });
  $inputPattern = input<'letters' | 'numbers' | 'alphanumeric' | null>(null, { alias: 'inputPattern' });
  $maxLength = input<number | null>(null, { alias: 'maxLength' });
  $isInvalid = input<boolean>(false, { alias: 'isInvalid' });

  // Outputs
  override valueChange = output<string>();

  // Signals / atributos
  override $controlValue = signal<string>('');

  // Bindings
  @HostBinding('style.width')
  get hostWidth(): string { return this.$hostWidth(); }

  // Verificar si se emitirá el valor al iniciar
  constructor () {
    super();
    afterNextRender(() => {
      if (this.$emitAtStart()) {
        const value = (this.$value() !== undefined ? this.$value() : this.$controlValue()) as string;
        this.valueChange.emit(value);
      }
    });
  }

  // Maneja el evento de entrada de texto
  override onValueChange(event: Event) {
    const newValue = (event.target as HTMLInputElement).value;
    if (this.$value() !== undefined)
      this.$_value.set(newValue);
    this.updateValue(newValue);
    this.valueChange.emit(newValue);
  }

  // Previene caracteres no permitidos según el patrón
  onKeyDown(event: KeyboardEvent): void {
    const pattern = this.$inputPattern();
    if (!pattern) return;

    // Permitir teclas de control
    if (event.ctrlKey || event.metaKey || event.altKey ||
        ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const key = event.key;
    let allowed = false;

    switch (pattern) {
      case 'letters':
        allowed = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]$/.test(key);
        break;
      case 'numbers':
        allowed = /^[0-9]$/.test(key);
        break;
      case 'alphanumeric':
        allowed = /^[a-zA-Z0-9]$/.test(key);
        break;
    }

    if (!allowed) {
      event.preventDefault();
    }
  }

  // Maneja el evento paste para filtrar caracteres no permitidos
  onPaste(event: ClipboardEvent): void {
    const pattern = this.$inputPattern();
    if (!pattern) return;

    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filteredText = '';

    switch (pattern) {
      case 'letters':
        filteredText = pastedText.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
        break;
      case 'numbers':
        filteredText = pastedText.replace(/[^0-9]/g, '');
        break;
      case 'alphanumeric':
        filteredText = pastedText.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        break;
    }

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = input.value;
    const newValue = currentValue.substring(0, start) + filteredText + currentValue.substring(end);

    input.value = newValue;
    this.updateValue(newValue);
    this.valueChange.emit(newValue);
  }
}
